/**
 * Community permission bitflags. Stored as an integer on each role.
 * The community owner and holders of ADMINISTRATOR implicitly have every permission.
 *
 * Bits are append-only: never renumber an existing flag, roles store them.
 */
export const Permission = {
  VIEW_CHANNELS: 1 << 0,
  SEND_MESSAGES: 1 << 1,
  ATTACH_FILES: 1 << 2,
  ADD_REACTIONS: 1 << 3,
  MENTION_EVERYONE: 1 << 4,
  MANAGE_MESSAGES: 1 << 5,
  MANAGE_CHANNELS: 1 << 6,
  MANAGE_ROLES: 1 << 7,
  MANAGE_COMMUNITY: 1 << 8,
  KICK_MEMBERS: 1 << 9,
  BAN_MEMBERS: 1 << 10,
  CREATE_INVITES: 1 << 11,
  PUBLISH_TO_FEED: 1 << 12,
  CONNECT_VOICE: 1 << 13,
  SPEAK: 1 << 14,
  VIEW_AUDIT_LOG: 1 << 15,
  MANAGE_INVITES: 1 << 16,
  PIN_MESSAGES: 1 << 17,
  BYPASS_SLOW_MODE: 1 << 18,
  POST_ANNOUNCEMENTS: 1 << 19,
  TIMEOUT_MEMBERS: 1 << 20,
  MUTE_MEMBERS: 1 << 21,
  DISCONNECT_MEMBERS: 1 << 22,
  READ_MESSAGE_HISTORY: 1 << 23,
  MANAGE_WEBHOOKS: 1 << 24,
  MANAGE_EXPRESSIONS: 1 << 25,
  USE_APPLICATION_COMMANDS: 1 << 26,
  MANAGE_EVENTS: 1 << 27,
  MANAGE_THREADS: 1 << 28,
  CREATE_THREADS: 1 << 29,
  ADMINISTRATOR: 1 << 30,
  /**
   * Bit 31: stored as a negative int32 (-2147483648). Bitwise ops in JS and
   * Postgres both treat it as the sign bit, so checks work unchanged; validators
   * must accept the full int32 range rather than min(0).
   */
  STREAM: 1 << 31,
} as const;

export type PermissionKey = keyof typeof Permission;

export const ALL_PERMISSIONS = Object.values(Permission).reduce((a, b) => a | b, 0);

export const DEFAULT_MEMBER_PERMISSIONS =
  Permission.VIEW_CHANNELS |
  Permission.READ_MESSAGE_HISTORY |
  Permission.SEND_MESSAGES |
  Permission.ATTACH_FILES |
  Permission.ADD_REACTIONS |
  Permission.CREATE_INVITES |
  Permission.CONNECT_VOICE |
  Permission.SPEAK |
  Permission.USE_APPLICATION_COMMANDS |
  Permission.CREATE_THREADS |
  Permission.STREAM;

export type PermissionGroup = "General" | "Membership" | "Text" | "Voice" | "Advanced";

export interface PermissionInfo {
  key: PermissionKey;
  label: string;
  description: string;
  group: PermissionGroup;
  /** Whether the permission can be overridden per channel. */
  channel: boolean;
}

export const PERMISSION_GROUPS: PermissionGroup[] = ["General", "Membership", "Text", "Voice", "Advanced"];

export const PERMISSION_INFO: PermissionInfo[] = [
  { group: "General", key: "VIEW_CHANNELS", label: "View channels", description: "See channels. Turn off per channel to hide it from a role.", channel: true },
  { group: "General", key: "MANAGE_CHANNELS", label: "Manage channels", description: "Create, edit, reorder and delete channels and categories.", channel: true },
  { group: "General", key: "MANAGE_ROLES", label: "Manage roles", description: "Create and edit roles below their highest role, and assign them.", channel: false },
  { group: "General", key: "MANAGE_COMMUNITY", label: "Manage community", description: "Edit the profile, appearance, access and safety settings.", channel: false },
  { group: "General", key: "VIEW_AUDIT_LOG", label: "View audit log", description: "See who changed what in the community.", channel: false },
  { group: "General", key: "MANAGE_WEBHOOKS", label: "Manage webhooks", description: "Create, edit and delete webhooks and integrations.", channel: true },
  { group: "General", key: "MANAGE_EXPRESSIONS", label: "Manage emojis and stickers", description: "Upload, rename and remove custom emojis and stickers.", channel: false },
  { group: "General", key: "MANAGE_EVENTS", label: "Manage events", description: "Create, edit and cancel community events.", channel: false },

  { group: "Membership", key: "CREATE_INVITES", label: "Create invites", description: "Create invite links and invite people directly.", channel: false },
  { group: "Membership", key: "MANAGE_INVITES", label: "Manage invites", description: "See and revoke every invite link.", channel: false },
  { group: "Membership", key: "KICK_MEMBERS", label: "Kick members", description: "Remove members below their highest role.", channel: false },
  { group: "Membership", key: "BAN_MEMBERS", label: "Ban members", description: "Remove members and stop them from rejoining.", channel: false },
  { group: "Membership", key: "TIMEOUT_MEMBERS", label: "Time out members", description: "Stop members from chatting, reacting or speaking for a while.", channel: false },

  { group: "Text", key: "SEND_MESSAGES", label: "Send messages", description: "Send messages in text and voice channel chats.", channel: true },
  { group: "Text", key: "READ_MESSAGE_HISTORY", label: "Read message history", description: "Read messages sent before they opened the channel.", channel: true },
  { group: "Text", key: "ATTACH_FILES", label: "Attach files", description: "Upload images to channels.", channel: true },
  { group: "Text", key: "ADD_REACTIONS", label: "Add reactions", description: "Add new reactions to messages.", channel: true },
  { group: "Text", key: "MENTION_EVERYONE", label: "Mention @everyone", description: "Notify every member who can see the channel.", channel: true },
  { group: "Text", key: "POST_ANNOUNCEMENTS", label: "Post announcements", description: "Send messages in announcement channels.", channel: true },
  { group: "Text", key: "PIN_MESSAGES", label: "Pin messages", description: "Pin and unpin messages.", channel: true },
  { group: "Text", key: "MANAGE_MESSAGES", label: "Manage messages", description: "Delete and pin messages from other members.", channel: true },
  { group: "Text", key: "BYPASS_SLOW_MODE", label: "Bypass slow mode", description: "Ignore slow mode and blocked words.", channel: true },
  { group: "Text", key: "USE_APPLICATION_COMMANDS", label: "Use commands", description: "Use slash commands from the platform and installed apps.", channel: true },
  { group: "Text", key: "CREATE_THREADS", label: "Create forum posts", description: "Start new posts in forum and media channels.", channel: true },
  { group: "Text", key: "MANAGE_THREADS", label: "Manage forum posts", description: "Lock, pin and delete posts in forum and media channels.", channel: true },
  { group: "Text", key: "PUBLISH_TO_FEED", label: "Publish to feed", description: "Publish posts on behalf of the community.", channel: false },

  { group: "Voice", key: "CONNECT_VOICE", label: "Connect", description: "Join voice channels.", channel: true },
  { group: "Voice", key: "SPEAK", label: "Speak", description: "Transmit audio in voice channels.", channel: true },
  { group: "Voice", key: "STREAM", label: "Share screen", description: "Share their screen in voice channels and on stage.", channel: true },
  { group: "Voice", key: "MUTE_MEMBERS", label: "Mute members", description: "Mute other people in voice for everyone.", channel: true },
  { group: "Voice", key: "DISCONNECT_MEMBERS", label: "Disconnect members", description: "Remove people from voice channels.", channel: true },

  { group: "Advanced", key: "ADMINISTRATOR", label: "Administrator", description: "Every permission, in every channel. Grant with care.", channel: false },
];

export function hasPermission(perms: number, flag: number): boolean {
  if ((perms & Permission.ADMINISTRATOR) === Permission.ADMINISTRATOR) return true;
  return (perms & flag) === flag;
}

/* ───────────── Role presets ───────────── */

export type RolePresetKey = "member" | "moderator" | "manager" | "admin" | "custom";

export interface RolePreset {
  key: RolePresetKey;
  name: string;
  description: string;
  color: string | null;
  permissions: number;
  hoist: boolean;
}

const MODERATOR_PERMISSIONS =
  DEFAULT_MEMBER_PERMISSIONS |
  Permission.MANAGE_MESSAGES |
  Permission.PIN_MESSAGES |
  Permission.BYPASS_SLOW_MODE |
  Permission.MENTION_EVERYONE |
  Permission.KICK_MEMBERS |
  Permission.TIMEOUT_MEMBERS |
  Permission.MUTE_MEMBERS |
  Permission.DISCONNECT_MEMBERS |
  Permission.VIEW_AUDIT_LOG |
  Permission.MANAGE_THREADS;

const MANAGER_PERMISSIONS =
  MODERATOR_PERMISSIONS |
  Permission.BAN_MEMBERS |
  Permission.MANAGE_CHANNELS |
  Permission.MANAGE_ROLES |
  Permission.MANAGE_INVITES |
  Permission.POST_ANNOUNCEMENTS |
  Permission.PUBLISH_TO_FEED |
  Permission.MANAGE_WEBHOOKS |
  Permission.MANAGE_EXPRESSIONS |
  Permission.MANAGE_EVENTS;

export const ROLE_PRESETS: RolePreset[] = [
  { key: "member", name: "Member", description: "Chat, react, share images and join voice.", color: "#5fb6e8", permissions: DEFAULT_MEMBER_PERMISSIONS, hoist: false },
  { key: "moderator", name: "Moderator", description: "Keep conversations healthy: manage messages, kick and time out.", color: "#4cc38a", permissions: MODERATOR_PERMISSIONS, hoist: true },
  { key: "manager", name: "Manager", description: "Run the community: channels, roles, bans and announcements.", color: "#e8b04c", permissions: MANAGER_PERMISSIONS, hoist: true },
  { key: "admin", name: "Administrator", description: "Every permission in every channel.", color: "#ef7b5b", permissions: Permission.ADMINISTRATOR, hoist: true },
  { key: "custom", name: "New role", description: "Start from nothing and pick permissions yourself.", color: null, permissions: 0, hoist: false },
];

/* ───────────── Channel overrides ───────────── */

export interface PermissionOverwrite {
  roleId: string;
  allow: number;
  deny: number;
}

/** Per-member override on a channel, applied after every role override (e.g. ticket channels). */
export interface MemberOverwrite {
  userId: string;
  allow: number;
  deny: number;
}

/**
 * Effective permissions inside a channel, Discord-style:
 * base role permissions → @everyone override → combined overrides of the member's other roles
 * → the member's own override.
 * Owners and administrators bypass overrides entirely.
 */
export function computeChannelPermissions(opts: {
  base: number;
  bypass: boolean;
  defaultRoleId: string | null;
  memberRoleIds: string[];
  overwrites: PermissionOverwrite[];
  /** The member's own override, applied last so it beats every role. */
  memberOverwrite?: { allow: number; deny: number } | null;
}): number {
  if (opts.bypass || hasPermission(opts.base, Permission.ADMINISTRATOR)) return ALL_PERMISSIONS;
  let p = opts.base;
  const everyone = opts.overwrites.find((o) => o.roleId === opts.defaultRoleId);
  if (everyone) p = (p & ~everyone.deny) | everyone.allow;
  let allow = 0;
  let deny = 0;
  for (const o of opts.overwrites) {
    if (o.roleId === opts.defaultRoleId || !opts.memberRoleIds.includes(o.roleId)) continue;
    allow |= o.allow;
    deny |= o.deny;
  }
  p = (p & ~deny) | allow;
  if (opts.memberOverwrite) p = (p & ~opts.memberOverwrite.deny) | opts.memberOverwrite.allow;
  return p;
}

/** Permissions that can be overridden per channel, as a mask. */
export const CHANNEL_PERMISSION_MASK = PERMISSION_INFO.filter((p) => p.channel).reduce((a, p) => a | Permission[p.key], 0);

/* ───────────── Platform roles ───────────── */

/** Platform-wide staff roles. Separate from community roles; only admins can grant them. */
export const PLATFORM_ROLES = ["user", "moderator", "admin"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export function isPlatformStaff(role: PlatformRole | null | undefined) {
  return role === "moderator" || role === "admin";
}
